import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OrganisationRoleConstants, OrganisationTypeConstants, ProjectAccessConstants } from "../../../../constants";
import { SearchProjectOrganisationInvitations } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { getSettingsProjectAccessRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";

const LOAD_ERROR_TOAST = {
  key: "dashboard-project-invitations-load-error",
  message: "Failed to load project invitations",
};

interface UseProjectInvitationBannerReturnData {
  // Lower-cased, and only set when there is a pending invitation the user can act on.
  invitationType?: string;
  navigateToProjectInvitations: () => void;
  corners: "rounded" | "default";
}

export const useProjectInvitationBanner = (): UseProjectInvitationBannerReturnData => {
  const { currentOrganisationUuid, currentUserRole, currentUserType } = useAuth();
  const navigate = useNavigate();
  const [invitationType, setInvitationType] = useState<string>();

  const canRespond =
    currentUserRole === OrganisationRoleConstants.OWNER || currentUserRole === OrganisationRoleConstants.ADMIN;

  useEffect(() => {
    if (!canRespond || !currentOrganisationUuid) return undefined;

    let isCurrent = true;
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.targetOrganisation = { uuid: { operator: "eq", value: currentOrganisationUuid } };
    filterCriteria.calculatedStatus = { operator: "eq", value: ProjectAccessConstants.STATUS_SENT };

    SearchProjectOrganisationInvitations({
      paging: { afterCursor: null, beforeCursor: null, limit: 1 },
      filter: { results: filterCriteria },
    })
      .then((response) => {
        if (!isCurrent) return;

        if (response.status === Status.Success) {
          setInvitationType(response.data?.results[0]?.type?.toLowerCase());
        } else {
          Toast.error(LOAD_ERROR_TOAST);
        }
      })
      .catch(() => {
        if (isCurrent) Toast.error(LOAD_ERROR_TOAST);
      });

    return () => {
      isCurrent = false;
    };
  }, [canRespond, currentOrganisationUuid]);

  const navigateToProjectInvitations = (): void => {
    navigate(getSettingsProjectAccessRoute(currentUserType as OrganisationTypeConstants));
  };

  return {
    invitationType,
    navigateToProjectInvitations,
    corners: currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default",
  };
};
